import axios, { AxiosInstance, AxiosResponse } from 'axios';
import DEPLOYMENT_CONFIG from '../config/deployment';
import { clearCrmAuthSession } from '../utils/authSession';

const resolveApiBaseUrl = () => DEPLOYMENT_CONFIG.getBackendUrl();

// Create axios instance (base URL resolved per request — CCC uses same-origin proxy)
const api: AxiosInstance = axios.create({
  baseURL: '',
  timeout: 30000, // Increased timeout to 30 seconds for complex analytics queries
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for CSRF
});

// CSRF token cache
let csrfToken: string | null = null;

// Function to get CSRF token
const getCsrfToken = async (): Promise<string | null> => {
  if (csrfToken) {
    return csrfToken;
  }
  
  try {
    const base = resolveApiBaseUrl();
    const csrfPath = base ? `${base.replace(/\/$/, '')}/api/csrf-token` : '/api/csrf-token';
    const response = await axios.get(csrfPath, {
      withCredentials: true
    });
    csrfToken = response.data.csrf_token;
    return csrfToken;
  } catch (error) {
    return null;
  }
};

// Request interceptor to add JWT token and CSRF token
api.interceptors.request.use(
  async (config) => {
    const base = resolveApiBaseUrl();
    if (base) {
      config.baseURL = base;
    }

    const token =
      localStorage.getItem('jwt_token') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('cognito_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    
    // Add CSRF token for POST, PUT, DELETE requests
    if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')) {
      const csrf = await getCsrfToken();
      if (csrf) {
        config.headers['X-CSRFToken'] = csrf;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and CSRF errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    // Handle CSRF token errors
    if (error.response?.status === 400 && error.response?.data?.message?.includes('CSRF')) {
      // Clear cached CSRF token and retry
      csrfToken = null;
      const originalRequest = error.config;
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const csrf = await getCsrfToken();
        if (csrf) {
          originalRequest.headers['X-CSRFToken'] = csrf;
        }
        return api(originalRequest);
      } else {
        // If retry already attempted or failed, logout automatically
        
        clearCrmAuthSession();
        
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      
      const currentToken = localStorage.getItem('jwt_token');
      if (currentToken) {
        try {
          // Try to refresh the token
          const refreshResponse = await api.post('/api/admin/refresh-token', {}, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          
          if (refreshResponse.data.success) {
            const newToken = refreshResponse.data.access_token;
            localStorage.setItem('jwt_token', newToken);
            
            // Retry the original request with new token
            const originalRequest = error.config;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Token refresh failed
        }
      }
      
      clearCrmAuthSession();
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  tenant?: string;
  business_area?: string;
  team_id?: number;
  avatar_url?: string;
  display_name?: string;
  /** Set when Cognito login was disabled via suspend or delete flow */
  cognito_access_suspended_at?: string | null;
}

export interface Lead {
  id: number;
  name: string;
  email?: string;  // Made optional for import scenarios
  phone?: string;
  company?: string;
  business_area: string;
  customer_type: string;
  lead_source?: string;
  lead_status?: string | null;
  section?: string;
  dif?: string;
  assigned_to?: number;
  created_by: number;
  product_type?: string;
  additional_cards?: string;
  assigned_user_name?: string;
  created_user_name?: string;
  created_at: string;
  updated_at: string;
  installation_date?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  current_provider?: string;
  contract_end_date?: string;
  monthly_spend?: number;
  service_interest?: string;
  last_contact_date?: string;
  next_follow_up?: string;
  lead_score?: number;
  priority?: string;
  lead_category?: number;
  tags?: string;
  pendente_reason?: string | null;
  is_duplicate?: boolean;
  duplicate_of_lead_id?: number;
  duplicate_status?: string;
  followup_note?: string | null;
  seminteresse_note?: string | null;
  informacao_errada_note?: string | null;  // Informação Errada field
  sem_cobertura_de?: string | null;  // Sem Cobertura field
  end_of_contract?: string | null;  // Fim da fidelização field
  last_contact_attempt?: string | null;  // Última tentativa de Contacto Falhada field
  _displayOrder?: number; // Internal field to maintain backend ordering
}

export interface Activity {
  id: number;
  title: string;
  activity_type: string;
  priority: string;
  status: string;
  description?: string;
  due_date?: string;
  completed_date?: string;
  duration_minutes?: number;
  lead_id?: number;
  account_id?: number;
  contact_id?: number;
  deal_id?: number;
  assigned_to?: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  subject?: string;
  outcome?: string;
  next_action?: string;
  reminder_enabled?: boolean;
  reminder_time?: string;
  notes?: string;
  tags?: string;
  importance_level?: number;
  persons_of_interest?: number[];
}

export interface Team {
  id: number;
  name: string;
  parent_id?: number;
  team_leader_id?: number;
  members: User[];
  children: Team[];
  description?: string;
  business_area?: string;
  created_at?: string;
  updated_at?: string;
  is_active?: boolean;
  is_hidden?: boolean;
  member_count?: number;
  children_count?: number;
}

export interface Notification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  priority: string;
  read: boolean;
  created_at: string;
  expires_at: string;
  is_expired?: boolean;
  task_id?: number;
  lead_id?: number;
  account_id?: number;
  deal_id?: number;
  activity_id?: number;
  action_url?: string;
  meta_data?: any;
}

export interface AssignmentHistory {
  id: number;
  lead_id: number;
  previous_assigned_to?: number;
  assigned_to: number;
  assigned_by: number;
  assigned_at: string;
}

// Vodafone Document Generation Types
export interface VodafoneTemplate {
  name: string;
  filename: string;
  display_name: string;
}

export interface VodafoneField {
  name: string;
  type: string;
  required: boolean;
  position?: { x: number; y: number; width?: number; height?: number };
  templateName?: string;  // Added for multiple template support
  originalName?: string;  // Added for multiple template support
  label?: string; // User-friendly label for display
  options?: string[]; // Options for dropdown fields (/Ch type)
  group?: string; // Field grouping (e.g., 'operadora_origem', 'data_hora')
  validation?: {
    type: string;
    pattern: string;
    message?: string;
  };
  export_value?: string; // Export value for checkbox fields
  related_field?: string; // Related field from another template for auto-fill
}

export interface VodafoneDocument {
  id: number;
  lead_id: number;
  user_id: number;
  document_type: string;
  file_path: string;
  field_data: Record<string, any>;
  created_at: string;
  expires_at: string;
  is_expired: boolean;
  days_until_expiry: number;
}

export interface FeedEventAnnotationRow {
  id?: number;
  event_type: string;
  event_key: string;
  note_text: string;
  updated_by_name?: string | null;
  updated_at?: string | null;
}

export interface MonitoringInvestigateMarcacaoRow {
  id: number;
  event_id: string;
  type: 'marcacao';
  created_at: string | null;
  created_by: { id: number | null; name: string };
  event_date: string | null;
  deleted_at: string | null;
  is_cancelled: boolean;
  produto?: string;
  nome_empresa?: string;
  nome_responsavel?: string;
  contacto_cliente?: string;
  assigned_to?: string[];
  assigned_user_ids?: number[];
}

export interface CallCenterTeamUserDto {
  id: number;
  name: string;
  email?: string | null;
  role?: string | null;
  avatar_url?: string | null;
  cognito_access_suspended_at?: string | null;
  is_hidden?: boolean;
  user_id?: number;
  sort_order?: number;
  reports_to_user_id?: number | null;
  letscall?: string | null;
}

export interface CallCenterTeamDto {
  id: number;
  name: string;
  lead_user_id: number | null;
  sort_order: number;
  color_hue: number;
  members: CallCenterTeamUserDto[];
}

// API Service Class
class ApiService {
  // Authentication
  async login(username: string, password: string): Promise<{ success?: boolean; token?: string; access_token?: string; jwt_token?: string; user?: User; requires_totp?: boolean; login_token?: string; message?: string; tenant?: string; challenge?: string; session?: string }> {
    try {
      // Use Cognito-backed login endpoint
      const response = await api.post('/api/cognito/login', { username, password });
      const { success, access_token, token, jwt_token, user, requires_totp, login_token, message, tenant, challenge, session } = response.data;
      
      // Prefer backend-issued JWT for API auth; keep Cognito token separately
      const finalToken = jwt_token || token || access_token;
      
      if (!challenge && finalToken && user) {
        localStorage.setItem('jwt_token', finalToken);
        localStorage.setItem('user', JSON.stringify(user));
      }

      // Store tenant for routing: prefer explicit tenant from response, then user.tenant; clear stale when missing
      try {
        const resolvedTenant = tenant || (user && (user as any).tenant) || '';
        if (resolvedTenant && !challenge) {
          localStorage.setItem('user_tenant', resolvedTenant);
        } else if (!challenge) {
          localStorage.removeItem('user_tenant');
        }
      } catch {}

      // Store Cognito access token for flows that require it (optional)
      if (access_token && !challenge) {
        localStorage.setItem('cognito_access_token', access_token);
      }
      
      return { success, token: finalToken, access_token, jwt_token, user, requires_totp, login_token, message, tenant, challenge, session };
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async respondToAuthChallenge(username: string, session: string, newPassword: string, challengeName: string = 'NEW_PASSWORD_REQUIRED'): Promise<{ success?: boolean; token?: string; access_token?: string; jwt_token?: string; user?: User; message?: string; tenant?: string }> {
    try {
      const response = await api.post('/api/cognito/respond_to_auth_challenge', {
        username,
        session,
        new_password: newPassword,
        challenge_name: challengeName
      });
      const { success, access_token, token, jwt_token, user, message, tenant } = response.data;
      
      // Prefer backend-issued JWT for API auth; keep Cognito token separately
      const finalToken = jwt_token || token || access_token;
      
      if (finalToken && user) {
        localStorage.setItem('jwt_token', finalToken);
        localStorage.setItem('user', JSON.stringify(user));
      }

      // Store tenant for routing
      try {
        const resolvedTenant = tenant || (user && (user as any).tenant) || '';
        if (resolvedTenant) {
          localStorage.setItem('user_tenant', resolvedTenant);
        } else {
          localStorage.removeItem('user_tenant');
        }
      } catch {}

      // Store Cognito access token
      if (access_token) {
        localStorage.setItem('cognito_access_token', access_token);
      }
      
      return { success, token: finalToken, access_token, jwt_token, user, message, tenant };
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    clearCrmAuthSession();
    csrfToken = null;
  }

  async getCsrfToken(): Promise<string | null> {
    return await getCsrfToken();
  }

  async refreshToken(): Promise<{ success?: boolean; token?: string; user?: User; message?: string }> {
    try {
      const response = await api.post('/api/admin/refresh-token');
      const { success, access_token, user, message } = response.data;
      
      if (access_token && user) {
        localStorage.setItem('jwt_token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return { success, token: access_token, user, message };
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async verifyTOTP(username: string, loginToken: string, totpCode: string): Promise<{ success?: boolean; token?: string; access_token?: string; user?: User; message?: string; tenant?: string }> {
    try {
      const response = await api.post('/api/admin/login/verify', {
        username,
        login_token: loginToken,
        totp_code: totpCode
      });
      
      // Handle both access_token and token for backward compatibility
      const { success, access_token, token, user, message, tenant } = response.data;
      const finalToken = access_token || token;
      
      if (finalToken && user) {
        localStorage.setItem('jwt_token', finalToken);
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return { success, token: finalToken, user, message, tenant };
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  // Users
  async getUsers(): Promise<User[]> {
    const response = await api.get('/api/users');
    return response.data.users;
  }

  async getAssignableUsers(): Promise<User[]> {
    const response = await api.get('/api/leads/assignable-users');
    return response.data.users;
  }

  async getViewableUsers(): Promise<User[]> {
    const response = await api.get('/api/leads/viewable-users');
    return response.data.users;
  }

  async createUser(userData: Partial<User>): Promise<{ success: boolean; message: string; user?: User }> {
    const response = await api.post('/api/users', userData);
    return response.data;
  }

  async updateUser(id: number, userData: Partial<User>): Promise<{ success: boolean; message: string; user?: User }> {
    const response = await api.put(`/api/users/${id}`, userData);
    return response.data;
  }

  async updateUserEmail(id: number, email: string): Promise<{ success: boolean; message: string; user?: User }> {
    const response = await api.put(`/api/users/${id}/update-email`, { email });
    return response.data;
  }

  async updateCurrentUser(userData: Partial<User>): Promise<{ success: boolean; message: string; user?: User }> {
    try {
      const response = await api.put('/api/users/me', userData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async deleteUser(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/users/${id}`);
    return response.data;
  }

  async suspendUserCognitoAccess(id: number): Promise<{ success: boolean; message: string; user?: User }> {
    const response = await api.post(`/api/users/${id}/suspend-cognito-access`, {});
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const requestData: any = {
        new_password: newPassword
      };
      
      // Only include current_password if it's provided
      if (currentPassword && currentPassword.trim() !== '') {
        requestData.current_password = currentPassword;
      }
      
      const response = await api.put('/api/users/me', requestData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  // Avatar upload
  async uploadAvatar(file: File): Promise<{ success: boolean; avatar_url?: string; message?: string }> {
    const formData = new FormData();
    formData.append('avatar', file);
    
          const response = await api.post('/api/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Leads
  async getLeads(business_area?: string, user_id?: number): Promise<Lead[]> {
    const params: any = {};
    if (business_area) params.business_area = business_area;
    if (user_id) params.user_id = user_id;
    const response = await api.get('/api/leads', { params });
    return response.data;
  }

  async getLead(id: number): Promise<Lead> {
    const response = await api.get(`/api/leads/${id}`);
    return response.data;
  }

  async createLead(leadData: Partial<Lead>): Promise<Lead> {
    const response = await api.post('/api/leads', leadData);
    return response.data;
  }

  async updateLead(id: number, leadData: Partial<Lead>): Promise<Lead> {
    const response = await api.put(`/api/leads/${id}`, leadData);
    return response.data;
  }

  async batchAssignLeads(leadIds: number[], assignedTo: number, assignedBy: number): Promise<any> {
    const response = await api.post('/api/leads/batch-assign', {
      lead_ids: leadIds,
      assigned_to: assignedTo,
      assigned_by: assignedBy
    });
    return response.data;
  }

  async deleteLead(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/api/leads/${id}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async getAssignmentHistory(leadId: number): Promise<AssignmentHistory[]> {
    const response = await api.get(`/api/leads/${leadId}/assignment-history`);
    return response.data.assignment_history;
  }

  // Lead Import
  async importTelecomLeads(file: File, businessArea?: string, leadCategory?: number): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (businessArea) {
        formData.append('business_area', businessArea);
      }
      if (leadCategory !== undefined) {
        formData.append('lead_category', leadCategory.toString());
      }
      const response = await api.post('/api/leads/import/telecom', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async importLeadsFormat(file: File, businessArea?: string, leadCategory?: number): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (businessArea) {
        formData.append('business_area', businessArea);
      }
      if (leadCategory !== undefined) {
        formData.append('lead_category', leadCategory.toString());
      }
      const response = await api.post('/api/leads/import/leads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async importEnergyLeads(file: File, leadCategory?: number): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (leadCategory !== undefined) {
      formData.append('lead_category', leadCategory.toString());
    }
    const response = await api.post('/api/leads/import/energy', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async importIntraFormat(file: File, businessArea?: string, leadCategory?: number): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (businessArea) {
        formData.append('business_area', businessArea);
      }
      if (leadCategory !== undefined) {
        formData.append('lead_category', leadCategory.toString());
      }
      
      const response = await api.post('/api/leads/import/intra', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveImportedLeads(importType: 'telecom' | 'energy', leads: any[]): Promise<any> {
    const response = await api.post(`/leads/import/${importType}/save`, { leads });
    return response.data;
  }

  // Duplicate Lead Management
  async getDuplicateLeads(): Promise<any> {
    const response = await api.get('/api/leads/duplicates');
    return response.data;
  }

  async resolveDuplicateLead(duplicateId: number, action: 'edit' | 'delete' | 'merge', data?: any): Promise<any> {
    const response = await api.post(`/api/leads/duplicates/${duplicateId}/resolve`, {
      action,
      ...(action === 'edit' && { update_data: data }),
      ...(action === 'merge' && { merge_data: data })
    });
    return response.data;
  }

  async getDuplicateStats(): Promise<any> {
    const response = await api.get('/api/leads/duplicates/stats');
    return response.data;
  }

  // Import Audit Logs
  async getImportAuditLogs(): Promise<any> {
    const response = await api.get('/api/leads/import-audit-logs');
    return response.data;
  }

  async getDuplicatesAndAudit(): Promise<any> {
    const response = await api.get('/api/leads/duplicates-and-audit');
    return response.data;
  }

  async clearImportHistory(): Promise<any> {
    try {
      const response = await api.delete('/api/leads/clear-import-history');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Activities/Calendar
  async getActivities(userId?: number, teamId?: number): Promise<Activity[]> {
    const params: any = {};
    if (userId) params.user_id = userId;
    if (teamId) params.team_id = teamId;
    const response = await api.get('/api/activities', { params });
    return response.data.activities;
  }

  async getActivity(id: number): Promise<Activity> {
    const response = await api.get(`/api/activities/${id}`);
    return response.data.activity;
  }

  async createActivity(activityData: Partial<Activity>): Promise<Activity> {
    const response = await api.post('/api/activities', activityData);
    if (response.data.success) {
      return response.data.activity;
    } else {
      throw new Error(response.data.message || 'Failed to create activity');
    }
  }

  async updateActivity(id: number, activityData: Partial<Activity>): Promise<Activity> {
    const response = await api.put(`/api/activities/${id}`, activityData);
    return response.data.activity;
  }

  async deleteActivity(id: number): Promise<void> {
    await api.delete(`/api/activities/${id}`);
  }

  async getTeamCalendar(teamId: number): Promise<any> {
    const response = await api.get(`/api/teams/${teamId}/calendar`);
    return response.data;
  }

  async syncToExternalCalendar(userId: number, activityIds: number[]): Promise<any> {
    const response = await api.post('/api/calendar/sync-external', {
      user_id: userId,
      activity_ids: activityIds
    });
    return response.data;
  }

  async getIcalCalendar(userId: number): Promise<Blob> {
    const response = await api.get(`/api/calendar/ical/${userId}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getWebcalCalendar(userId: number): Promise<string> {
    const response = await api.get(`/api/calendar/webcal/${userId}`);
    return response.data;
  }

  // Teams
  async getTeams(): Promise<Team[]> {
    const response = await api.get('/api/teams');
    const teamsTree = response.data.teams || [];
    
    // Flatten the hierarchical structure to show all teams (excluding hidden teams)
    const flattenTeams = (teams: Team[]): Team[] => {
      let flatTeams: Team[] = [];
      for (const team of teams) {
        // Filter out hidden teams
        if (!team.is_hidden) {
          flatTeams.push(team);
          if (team.children && team.children.length > 0) {
            flatTeams = flatTeams.concat(flattenTeams(team.children));
          }
        }
      }
      return flatTeams;
    };
    
    return flattenTeams(teamsTree);
  }

  async getTeam(id: number): Promise<Team> {
    const response = await api.get(`/api/teams/${id}`);
    return response.data.team;
  }

  async createTeam(teamData: Partial<Team>): Promise<Team> {
    const response = await api.post('/api/teams', teamData);
    return response.data.team;
  }

  async updateTeam(id: number, teamData: Partial<Team>): Promise<Team> {
    const response = await api.put(`/api/teams/${id}`, teamData);
    return response.data.team;
  }

  async deleteTeam(id: number): Promise<void> {
    await api.delete(`/api/teams/${id}`);
  }

  async getTeamMembers(teamId: number): Promise<User[]> {
    const response = await api.get(`/api/teams/${teamId}/members`);
    return response.data.members || [];
  }

  async addTeamMember(teamId: number, userId: number): Promise<void> {
    await api.post(`/api/teams/${teamId}/members`, { user_id: userId });
  }

  async removeTeamMember(teamId: number, userId: number): Promise<void> {
    await api.delete(`/api/teams/${teamId}/members/${userId}`);
  }

  // Notifications
  async getNotifications(userId: number): Promise<Notification[]> {
    const response = await api.get(`/api/notifications?user_id=${userId}`);
    return response.data.notifications;
  }

  async markNotificationAsRead(id: number): Promise<void> {
    await api.post(`/api/notifications/${id}/read`);
  }

  async markAllNotificationsAsRead(userId: number): Promise<void> {
    await api.post('/api/notifications/read-all', { user_id: userId });
  }

  async deleteNotification(id: number): Promise<void> {
    await api.delete(`/api/notifications/${id}`);
  }

  async clearNotifications(userId: number, scope: 'new' | 'all'): Promise<void> {
    await api.post('/api/notifications/clear', { user_id: userId, scope });
  }

  async getNotificationStats(userId: number): Promise<any> {
    const response = await api.get(`/api/notifications/stats?user_id=${userId}`);
    return response.data;
  }

  // Dashboard
  async getDashboardStats(userId: number, businessArea?: string): Promise<any> {
    const params: any = { user_id: userId };
    if (businessArea) params.business_area = businessArea;
    const response = await api.get('/api/dashboard/stats', { params });
    return response.data;
  }

  async getRecentLeads(userId: number, limit: number = 10): Promise<Lead[]> {
    const response = await api.get(`/api/dashboard/recent-leads?user_id=${userId}&limit=${limit}`);
    return response.data;
  }

  async getRecentNotifications(userId: number, limit: number = 10): Promise<Notification[]> {
    const response = await api.get(`/api/dashboard/recent-notifications?user_id=${userId}&limit=${limit}`);
    return response.data;
  }

  // Admin Analytics API methods
  async getAdminOverview(params?: { user_id: number; start_date?: string; end_date?: string }): Promise<any> {
    const response = await api.get('/api/admin/analytics/overview', { params });
    return response.data;
  }

  async getUserPerformance(params: { user_id: number; target_user_id: number; start_date?: string; end_date?: string }): Promise<any> {
    const response = await api.get('/api/admin/analytics/user-performance', { params });
    return response.data;
  }

  async getTeamPerformance(params: { user_id: number; team_id: number; start_date?: string; end_date?: string }): Promise<any> {
    const response = await api.get('/api/admin/analytics/team-performance', { params });
    return response.data;
  }

  async getLeadsTable(params: { 
    user_id: number; 
    page?: number; 
    per_page?: number; 
    status?: string; 
    assigned_to?: number; 
    created_by?: number; 
    created_date?: string; 
    updated_date?: string; 
    business_area?: string; 
    customer_type?: string;
    section?: string;
    current_provider?: string;
    city?: string;
    sem_cobertura_de?: string;
    name?: string;
    email?: string;
    phone?: string;
    company?: string;
  }): Promise<any> {
    const response = await api.get('/api/admin/analytics/leads-table', { params });
    return response.data;
  }

  async getUsersForAnalysis(params: { user_id: number }): Promise<any> {
    const response = await api.get('/api/admin/analytics/users', { params });
    return response.data;
  }

  async getTeamsForAnalysis(params: { user_id: number }): Promise<any> {
    const response = await api.get('/api/admin/analytics/teams', { params });
    return response.data;
  }

  async verifyAdminAnalyticsAccess(): Promise<{ allowed: boolean }> {
    const response = await api.get('/api/admin/analytics/verify-access');
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/api/users/me');
    const data = response.data;
    return (data && (data.user ?? data)) as User;
  }

  // Advanced Lead Management - Notes
  async getLeadNotes(leadId: number): Promise<{ notes: any[] }> {
    const response = await api.get(`/api/leads/${leadId}/notes`);
    return response.data;
  }

  async createLeadNote(leadId: number, data: { title: string; content: string; importance_level: string; visibility: string }): Promise<{ note: any }> {
    const response = await api.post(`/api/leads/${leadId}/notes`, data);
    return response.data;
  }

  // Lead Files
  async getLeadFiles(leadId: number, params?: { stage?: string; field_name?: string }): Promise<{ success: boolean; files: any[] }> {
    const response = await api.get(`/api/leads/${leadId}/files`, { params });
    return response.data;
  }
  async uploadLeadFile(leadId: number, formData: FormData, onProgress?: (progress: number) => void): Promise<{ success: boolean; file?: any; message?: string }> {
    const response = await api.post(`/api/leads/${leadId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  }
  async deleteLeadFile(leadId: number, fileId: number): Promise<{ success: boolean; message?: string }> {
    const response = await api.delete(`/api/leads/${leadId}/files/${fileId}`);
    return response.data;
  }

  // Proposal Files (associated with marcações/proposals)
  async uploadProposalFile(marcacaoId: string | number, formData: FormData): Promise<{ success: boolean; file?: any; error?: string; message?: string }> {
    const response = await api.post(`/api/callcenter/proposals/${marcacaoId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async previewLeadFile(leadId: number, fileId: number): Promise<{ success: boolean; pdf_data: string } | { success: boolean; error: string; filename?: string; file_size?: number }> {
    try {
      const response = await api.get(`/api/leads/${leadId}/files/${fileId}/preview-base64`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: 'Erro ao carregar pré-visualização' };
    }
  }

  async downloadLeadFile(leadId: number, fileId: number): Promise<{ success: boolean; data: Blob }> {
    try {
      const response = await api.get(`/api/leads/${leadId}/files/${fileId}/download`, { responseType: 'blob' });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, data: new Blob() };
    }
  }

  // Notes
  async updateLeadNote(leadId: number, noteId: number, data: { title: string; content: string; importance_level: string; visibility: string }): Promise<{ note: any }> {
    const response = await api.put(`/api/leads/${leadId}/notes/${noteId}`, data);
    return response.data;
  }
  async deleteLeadNote(leadId: number, noteId: number): Promise<void> {
    await api.delete(`/api/leads/${leadId}/notes/${noteId}`);
  }
  async highlightLeadNote(leadId: number, noteId: number, data: { is_highlighted: boolean }): Promise<{ note: any }> {
    const response = await api.post(`/api/leads/${leadId}/notes/${noteId}/highlight`, data);
    return response.data;
  }

  async getHighlightedNotes(leadId: number): Promise<{ highlighted_notes: any[] }> {
    const response = await api.get(`/api/leads/${leadId}/notes/highlighted`);
    return response.data;
  }

  // Vodafone Document Generation
  async getVodafoneTemplates(): Promise<VodafoneTemplate[]> {
    const response = await api.get('/api/documents/templates');
    return response.data.templates;
  }
  async getVodafoneTemplateFields(templateName: string): Promise<VodafoneField[]> {
    const response = await api.get(`/api/documents/fields/${templateName}`);
    return response.data.fields;
  }
  async previewVodafoneDocument(documentType: string, fieldData: Record<string, any>, leadId?: number): Promise<string> {
    const requestData: any = { document_type: documentType, field_data: fieldData };
    if (leadId) {
      requestData.lead_id = leadId;
    }
    const response = await api.post('/api/documents/preview', requestData);
    return response.data.pdf_data;
  }
  async generateDocument(leadId: number, documentType: string, fieldData: Record<string, any>): Promise<{ document: { id: number; download_url: string; preview_url: string; message: string } }> {
    const response = await api.post('/api/documents/generate', { lead_id: leadId, template_name: documentType, field_data: fieldData });
    return response.data;
  }

  async generateVodafoneDocument(leadId: number, documentType: string, fieldData: Record<string, any>): Promise<{ document: { id: number; download_url: string; preview_url: string; message: string } }> {
    return this.generateDocument(leadId, documentType, fieldData);
  }
  async downloadVodafoneDocument(documentId: number): Promise<Blob> {
    const response = await api.get(`/api/documents/${documentId}/download`, { responseType: 'blob' });
    return response.data;
  }

  // Lead Documents Management
  async getLeadDocuments(leadId: number): Promise<{ success: boolean; documents: VodafoneDocument[] }> {
    try {
      const response = await api.get(`/api/leads/${leadId}/documents`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async previewDocument(documentId: number): Promise<{ success: boolean; pdf_data: string } | { success: boolean; error: string; filename?: string; file_size?: number }> {
    try {
      const response = await api.get(`/api/documents/${documentId}/preview`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: 'Erro ao carregar pré-visualização' };
    }
  }

  async downloadDocument(documentId: number): Promise<{ success: boolean; data: Blob }> {
    try {
      const response = await api.get(`/api/documents/${documentId}/download`, { responseType: 'blob' });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, data: new Blob() };
    }
  }

  // Document Progress Methods
  async getDocumentPages(documentName: string): Promise<{ success: boolean; pages: any[] }> {
    try {
      const response = await api.get(`/api/document-progress/pages/${encodeURIComponent(documentName)}`);
      return response.data;
    } catch (error: any) {
      return { success: false, pages: [] };
    }
  }

  async getFieldValues(leadId: number): Promise<{ success: boolean; field_values: Record<string, any> }> {
    try {
      const response = await api.get(`/api/document-progress/field-values/${leadId}`);
      return response.data;
    } catch (error: any) {
      return { success: false, field_values: {} };
    }
  }

  async saveFieldValues(leadId: number, fieldValues: Record<string, any>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/api/document-progress/field-values/${leadId}`, { field_values: fieldValues });
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Erro ao guardar valores dos campos' };
    }
  }

  async getDocumentProgress(leadId: number): Promise<{ success: boolean; progress: Record<string, any> }> {
    try {
      const response = await api.get(`/api/document-progress/progress/${leadId}`);
      return response.data;
    } catch (error: any) {
      return { success: false, progress: {} };
    }
  }

  async saveDocumentProgress(leadId: number, documentType: string, currentPage: number, isCompleted: boolean = false): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/api/document-progress/progress/${leadId}`, {
        document_type: documentType,
        current_page: currentPage,
        is_completed: isCompleted
      });
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Erro ao guardar progresso' };
    }
  }

  async getAutoFillSuggestions(leadId: number, fieldNames: string[]): Promise<{ success: boolean; suggestions: Record<string, any> }> {
    try {
      const response = await api.post(`/api/document-progress/auto-fill/${leadId}`, { field_names: fieldNames });
      return response.data;
    } catch (error: any) {
      return { success: false, suggestions: {} };
    }
  }

  async cleanupProgress(leadId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/api/document-progress/cleanup/${leadId}`);
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Erro ao limpar progresso' };
    }
  }

  // Leaderboard methods
  async getTopTenLeaderboard(): Promise<{ success: boolean; leaderboard: any[] }> {
    try {
      const response = await api.get('/api/leaderboard/top-ten');
      return response.data;
    } catch (error: any) {
      return { success: false, leaderboard: [] };
    }
  }

  async getAllScores(): Promise<{ success: boolean; scores: any[] }> {
    try {
      const response = await api.get('/api/leaderboard/all-scores');
      return response.data;
    } catch (error: any) {
      return { success: false, scores: [] };
    }
  }

  async updateUserScore(userId: number, installationCount: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/api/leaderboard/update-score', {
        user_id: userId,
        installation_count: installationCount
      });
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Erro ao atualizar pontuação' };
    }
  }

  async sendEmailWithAttachments({ subject, body, html, documentIds, files, destinatario }: { subject: string; body: string; html?: string; documentIds?: number[]; files?: File[]; destinatario?: string }): Promise<{ success: boolean; message: string }> {
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('body', body);
    if (html) formData.append('html', html);
    if (destinatario) formData.append('destinatario', destinatario);
    if (documentIds) documentIds.forEach(id => formData.append('document_ids', id.toString()));
    if (files) files.forEach(file => formData.append('files', file));
    // Use the correct endpoint
    const response = await api.post('/api/send_email', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async submitReport(formData: FormData): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/api/submit-report', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // TaskMaster API methods
  async getTasks(params?: string): Promise<{ success: boolean; tasks: any[] }> {
    try {
      const url = params ? `/api/tasks?${params}` : '/api/tasks';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createTask(taskData: any): Promise<{ success: boolean; message: string; task?: any }> {
    try {
      const response = await api.post('/api/tasks', taskData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTask(taskId: number, taskData: any): Promise<{ success: boolean; message: string; task?: any }> {
    try {
      const response = await api.put(`/api/tasks/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteTask(taskId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/api/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async completeTask(taskId: number, data: { response_text?: string; feedback?: string }): Promise<{ success: boolean; message: string; task?: any }> {
    try {
      const response = await api.post(`/api/tasks/${taskId}/complete`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTaskStatistics(): Promise<{ success: boolean; statistics: any }> {
    try {
      const response = await api.get('/api/tasks/statistics');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAvailableUsers(): Promise<{ success: boolean; users: any[] }> {
    try {
      const response = await api.get('/api/tasks/available-users');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Altice Sales
  async createAlticeSale(saleData: any): Promise<{ success: boolean; message: string; id?: number; data?: any }> {
    try {
      const response = await api.post('/api/altice-sales', saleData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAlticeSalesByLead(leadId: number): Promise<any[]> {
    try {
      const response = await api.get(`/api/altice-sales/lead/${leadId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Solutio API methods - getUsersForSolutio (uses existing users endpoint)
  async getUsersForSolutio(): Promise<any> {
    try {
      const response = await api.get('/api/users');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getUserIncome(userId: number, year: number, month: number): Promise<any> {
    try {
      const response = await api.get(`/api/users/${userId}/income?year=${year}&month=${month}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getUserLeads(userId: number, year: number, month: number): Promise<any> {
    try {
      const response = await api.get(`/api/users/${userId}/leads?year=${year}&month=${month}&section=instalados`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAccountingStatus(userId: number, month: number, year: number, resolved: boolean): Promise<any> {
    try {
      const response = await api.put(`/api/admin/accounting-status`, {
        user_id: userId,
        month: month,
        year: year,
        resolved: resolved
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ----- CallCenter Monitoring (per (comercial, week) editable notes) -----
  async getMonitoringConflicts(weekStart: string): Promise<{
    success: boolean;
    week_start_date: string;
    week_end_date: string;
    summary: {
      conflict_count: number;
      users_affected: number;
      events_involved: number;
      duplicate_count?: number;
      duplicate_events?: number;
    };
    conflicts: Array<{
      user_id: string;
      user_name: string;
      date: string;
      overlap_minutes: number;
      events: Array<{
        id: string;
        user_id: string;
        type: string;
        date: string;
        hora_inicio: string;
        hora_fim: string;
        title: string;
        subtitle?: string | null;
        created_by_comercial?: boolean;
      }>;
    }>;
    duplicates?: Array<{
      matched_fields: string[];
      matched_field_labels: string[];
      date_min: string;
      date_max: string;
      span_days: number;
      events: Array<{
        id: string;
        date: string;
        hora_inicio: string;
        hora_fim: string;
        title: string;
        produto?: string;
        nome_empresa?: string;
        nome_responsavel?: string;
        morada?: string;
        contacto_cliente?: string;
        created_by?: number | null;
        created_by_name?: string | null;
        created_by_comercial?: boolean;
        user_ids?: string[];
        assigned_comercial_names?: string[];
      }>;
    }>;
  }> {
    const response = await api.get('/api/callcenter/monitoring/conflicts', {
      params: { week_start: weekStart },
    });
    return response.data;
  }

  async getMonitoringNotes(weekStart: string): Promise<{
    success: boolean;
    week_start_date: string;
    notes: Array<{
      id: number;
      comercial_user_id: number;
      week_start_date: string;
      prioridade_note: string;
      observacoes_note: string;
      created_at: string;
      updated_at: string;
      updated_by_user_id: number | null;
      updated_by_name: string | null;
    }>;
  }> {
    const response = await api.get('/api/callcenter/monitoring/notes', {
      params: { week_start: weekStart },
    });
    return response.data;
  }

  async getMonitoringCellPaints(weekStart: string): Promise<{
    success: boolean;
    week_start_date: string;
    paints: Array<{
      id: number;
      comercial_user_id: number;
      week_start_date: string;
      cell_key: string;
      cell_date?: string | null;
      cell_column?: string | null;
      color: string;
      updated_at: string | null;
      updated_by_user_id: number | null;
    }>;
  }> {
    const response = await api.get('/api/callcenter/monitoring/cell-paints', {
      params: { week_start: weekStart },
    });
    return response.data;
  }

  async batchUpsertMonitoringCellPaints(payload: {
    week_start_date: string;
    updates: Array<{
      comercial_user_id: number;
      cell_key: string;
      color: string | null;
    }>;
  }): Promise<{
    success: boolean;
    week_start_date: string;
    paints: Array<{
      id: number;
      comercial_user_id: number;
      week_start_date: string;
      cell_key: string;
      cell_date?: string | null;
      cell_column?: string | null;
      color: string;
      updated_at: string | null;
      updated_by_user_id: number | null;
    }>;
  }> {
    const response = await api.put('/api/callcenter/monitoring/cell-paints/batch', payload);
    return response.data;
  }

  async upsertMonitoringNote(payload: {
    comercial_user_id: number;
    week_start_date: string;
    prioridade_note?: string;
    observacoes_note?: string;
  }): Promise<{
    success: boolean;
    note: {
      id: number;
      comercial_user_id: number;
      week_start_date: string;
      prioridade_note: string;
      observacoes_note: string;
      created_at: string;
      updated_at: string;
      updated_by_user_id: number | null;
      updated_by_name: string | null;
    };
  }> {
    const response = await api.put('/api/callcenter/monitoring/notes', payload);
    return response.data;
  }

  async upsertMonitoringEquipa(payload: {
    comercial_user_id: number;
    equipa_note: string;
  }): Promise<{
    success: boolean;
    comercial_user_id: number;
    equipa_note: string;
  }> {
    const response = await api.put('/api/callcenter/monitoring/equipa', payload);
    return response.data;
  }

  async batchGetFeedEventAnnotations(
    items: Array<{ event_type: string; event_key: string }>,
  ): Promise<{
    success: boolean;
    annotations: FeedEventAnnotationRow[];
  }> {
    const response = await api.post('/api/callcenter/feed/annotations', { items });
    return response.data;
  }

  async upsertFeedEventAnnotation(payload: {
    event_type: string;
    event_key: string;
    note_text: string;
  }): Promise<{
    success: boolean;
    deleted?: boolean;
    annotation?: FeedEventAnnotationRow;
  }> {
    const response = await api.put('/api/callcenter/feed/annotations', payload);
    return response.data;
  }

  async searchMonitoringInvestigateMarcacoes(params: {
    nome_empresa?: string;
    morada?: string;
    codigo_postal?: string;
    contacto_cliente?: string;
    nome_responsavel?: string;
    comercial_user_id?: string;
    operador_id?: string;
    status?: 'all' | 'active' | 'deleted';
    produtos?: string[];
    event_start_date?: string;
    event_end_date?: string;
    created_start_date?: string;
    created_end_date?: string;
    deleted_start_date?: string;
    deleted_end_date?: string;
    limit?: number;
  }): Promise<{
    success: boolean;
    data: MonitoringInvestigateMarcacaoRow[];
    message?: string;
  }> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value == null || value === '') return;
      if (key === 'produtos' && Array.isArray(value)) {
        value.forEach((produto) => {
          if (produto) query.append('produtos', produto);
        });
        return;
      }
      query.set(key, String(value));
    });
    const response = await api.get(
      `/api/callcenter/monitoring/investigate/marcacoes?${query.toString()}`,
    );
    return response.data;
  }

  async getMonitoringInvestigateMarcacaoOverview(marcacaoId: string): Promise<{
    success: boolean;
    overview?: {
      marcacao_id: string;
      gestao_substage: string;
      gestao_substage_label: string;
      is_visita_valida: boolean;
      is_cancelled: boolean;
      created_at?: string | null;
      updated_at?: string | null;
      created_by?: { id?: number | null; name?: string | null };
      current_comerciais: Array<{ id: number; name: string }>;
      original_comerciais: Array<{ id: number; name: string }>;
      assignee_changed: boolean;
      timeline: Array<{
        at?: string | null;
        type: string;
        kind: 'status' | 'assignee' | 'created' | string;
        summary?: string;
        from_label?: string;
        to_label?: string;
        from_names?: string[];
        to_names?: string[];
        by_name?: string | null;
        inferred?: boolean;
      }>;
    };
    message?: string;
  }> {
    const response = await api.get(
      `/api/callcenter/monitoring/investigate/marcacoes/${encodeURIComponent(marcacaoId)}/overview`,
    );
    return response.data;
  }

  async getCallCenterTeams(): Promise<{
    success: boolean;
    teams: CallCenterTeamDto[];
    unassigned: CallCenterTeamUserDto[];
    message?: string;
  }> {
    const response = await api.get('/api/callcenter/teams');
    return response.data;
  }

  async syncCallCenterTeams(payload: {
    teams: Array<{
      id?: number | null;
      name: string;
      lead_user_id?: number | null;
      sort_order?: number;
      color_hue?: number;
      member_user_ids?: number[];
      members?: Array<{
        user_id: number;
        sort_order?: number;
        reports_to_user_id?: number | null;
      }>;
    }>;
  }): Promise<{
    success: boolean;
    teams: CallCenterTeamDto[];
    unassigned: CallCenterTeamUserDto[];
    message?: string;
  }> {
    const response = await api.put('/api/callcenter/teams', payload);
    return response.data;
  }

  async getCallCenterScenarios(params: {
    start_date: string;
    end_date: string;
  }): Promise<{
    success: boolean;
    scenarios: Array<{
      id: number;
      comercial_id: number;
      date: string;
      scenario_text: string;
      location?: string | null;
      title?: string | null;
      due_date?: string | null;
    }>;
    count?: number;
    message?: string;
  }> {
    const response = await api.get('/api/callcenter/scenarios', { params });
    return response.data;
  }

  async getCallCenterCycleGoal(): Promise<{
    success: boolean;
    goal: {
      id: number;
      target_avg_per_weekday: number;
      note?: string | null;
      updated_by_user_id?: number | null;
      updated_by_name?: string | null;
      created_at?: string | null;
      updated_at?: string | null;
    };
    message?: string;
  }> {
    const response = await api.get('/api/callcenter/cycle-goal');
    return response.data;
  }

  async putCallCenterCycleGoal(payload: {
    target_avg_per_weekday: number;
    note?: string | null;
  }): Promise<{
    success: boolean;
    goal: {
      id: number;
      target_avg_per_weekday: number;
      note?: string | null;
      updated_by_user_id?: number | null;
      updated_by_name?: string | null;
      created_at?: string | null;
      updated_at?: string | null;
    };
    message?: string;
  }> {
    const response = await api.put('/api/callcenter/cycle-goal', payload);
    return response.data;
  }

  async getCallCenterCicloCreationCounts(params: {
    start_date: string;
    end_date: string;
    created_by?: number[];
  }): Promise<{
    success: boolean;
    start_date?: string;
    end_date?: string;
    counts: Record<string, Record<string, number>>;
    message?: string;
  }> {
    const qs = new URLSearchParams();
    qs.set('start_date', params.start_date);
    qs.set('end_date', params.end_date);
    (params.created_by || []).forEach((id) => {
      if (id != null) qs.append('created_by', String(id));
    });
    const response = await api.get(
      `/api/callcenter/ciclo/creation-counts?${qs.toString()}`,
    );
    return response.data;
  }

  async getCallCenterQualidadeSeen(marcacaoIds?: string[]): Promise<{
    success: boolean;
    seen: Record<
      string,
      {
        id: number;
        marcacao_id: string;
        seen: boolean;
        seen_by_user_id?: number | null;
        seen_by_name?: string | null;
        seen_at?: string | null;
      }
    >;
    message?: string;
  }> {
    // POST body — cycle lists exceed GET query-string / request-line limits
    const response = await api.post('/api/callcenter/qualidade/seen', {
      marcacao_ids: marcacaoIds && marcacaoIds.length > 0 ? marcacaoIds : [],
    });
    return response.data;
  }

  async putCallCenterQualidadeSeen(payload: {
    marcacao_id: string;
    seen: boolean;
  }): Promise<{
    success: boolean;
    entry: {
      id: number;
      marcacao_id: string;
      seen: boolean;
      seen_by_user_id?: number | null;
      seen_by_name?: string | null;
      seen_at?: string | null;
    };
    message?: string;
  }> {
    const response = await api.put('/api/callcenter/qualidade/seen', payload);
    return response.data;
  }
}

// Create and export API service instance
const apiService = new ApiService();
export default apiService;
export { api };

// Main page content API
export const getMainPageContent = async (): Promise<any> => {
  try {
    const response = await api.get('/api/main-page-content');
    return response.data;
  } catch (error) {
    return {
      important_news: '',
      main_news: '',
      ticker_news: [],
      cartoon_image: null,
      last_updated: null,
      updated_by: null
    };
  }
};

export const updateMainPageContent = async (data: {
  important_news?: string;
  main_news?: string;
  ticker_news?: string[];
  cartoon_image?: string;
}): Promise<any> => {
  try {
    const response = await api.post('/api/main-page-content', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Profile API methods
export const getProfileUserInfo = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/user-info');
    return response.data.data; // Extract the actual user data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileCurrentIncome = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/income/current');
    return response.data.data; // Extract the actual data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileHistoricalIncome = async (months: number = 12): Promise<any> => {
  try {
    const response = await api.get(`/api/profile/income/historical?months=${months}`);
    return response.data.data; // Extract the actual data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileInstallationData = async (months: number = 12): Promise<any> => {
  try {
    const response = await api.get(`/api/profile/installation-data?months=${months}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProfileInstallationChart = async (months: number = 12): Promise<any> => {
  try {
    const response = await api.get(`/api/profile/installation-chart?months=${months}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Team Point Configuration API
export const getTeamPointConfig = async (teamId: number): Promise<any> => {
  try {
    const response = await api.get(`/api/team-point-config/${teamId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateTeamPointConfig = async (teamId: number, config: any): Promise<any> => {
  try {
    const response = await api.put(`/api/team-point-config/${teamId}`, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserTeamPointConfig = async (userId: number): Promise<any> => {
  try {
    const response = await api.get(`/api/team-point-config/user/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProfileBrackets = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/brackets');
    return response.data.data; // Extract the actual data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileRecommendations = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/recommendations');
    return response.data.data; // Extract the actual data from the response
  } catch (error) {
    throw error;
  }
};

export const getProfileSalesBrackets = async (): Promise<any> => {
  try {
    const response = await api.get('/api/profile/sales-brackets');
    return response.data.data; // Extract the data property from the response
  } catch (error) {
    throw error;
  }
};
